<template>
  <canvas ref="canvasRef" class="particle-canvas"></canvas>
</template>

<script setup>
import { ref, onMounted, onUnmounted, defineProps } from "vue";

const props = defineProps({
  count: { type: Number, default: 80 },
  speed: { type: Number, default: 0.3 },
  opacity: { type: Number, default: 0.6 },
  color: { type: String, default: "167, 139, 250" },
  minSize: { type: Number, default: 0.5 },
  maxSize: { type: Number, default: 2.5 },
});

const canvasRef = ref(null);
let animationFrameId = null;
let particles = [];

function initParticles(w, h) {
  particles = Array.from({ length: props.count }, () => ({
    x: Math.random() * w,
    y: Math.random() * h,
    vx: (Math.random() - 0.5) * props.speed,
    vy: (Math.random() - 0.5) * props.speed,
    size: props.minSize + Math.random() * (props.maxSize - props.minSize),
    alpha: 0.1 + Math.random() * props.opacity,
    pulse: Math.random() * Math.PI * 2,
  }));
}

function resize() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const parent = canvas.parentElement;
  const dpr = Math.min(window.devicePixelRatio, 2);
  canvas.width = parent.offsetWidth * dpr;
  canvas.height = parent.offsetHeight * dpr;
  canvas.style.width = parent.offsetWidth + "px";
  canvas.style.height = parent.offsetHeight + "px";
  const ctx = canvas.getContext("2d");
  ctx.scale(dpr, dpr);
  initParticles(parent.offsetWidth, parent.offsetHeight);
}

function animate() {
  const canvas = canvasRef.value;
  if (!canvas) return;
  const ctx = canvas.getContext("2d");
  const w = canvas.parentElement.offsetWidth;
  const h = canvas.parentElement.offsetHeight;

  ctx.clearRect(0, 0, w, h);

  particles.forEach((p) => {
    p.x += p.vx;
    p.y += p.vy;
    p.pulse += 0.01;

    if (p.x < -10) p.x = w + 10;
    if (p.x > w + 10) p.x = -10;
    if (p.y < -10) p.y = h + 10;
    if (p.y > h + 10) p.y = -10;

    const flickerAlpha = p.alpha * (0.6 + 0.4 * Math.sin(p.pulse));

    ctx.beginPath();
    ctx.arc(p.x, p.y, p.size, 0, Math.PI * 2);
    ctx.fillStyle = `rgba(${props.color}, ${flickerAlpha})`;
    ctx.fill();

    if (p.size > 1.5) {
      ctx.beginPath();
      ctx.arc(p.x, p.y, p.size * 3, 0, Math.PI * 2);
      ctx.fillStyle = `rgba(${props.color}, ${flickerAlpha * 0.1})`;
      ctx.fill();
    }
  });

  animationFrameId = requestAnimationFrame(animate);
}

onMounted(() => {
  const prefersReduced = window.matchMedia(
    "(prefers-reduced-motion: reduce)"
  ).matches;
  if (prefersReduced) return;

  resize();
  animate();
  window.addEventListener("resize", resize);
});

onUnmounted(() => {
  if (animationFrameId) cancelAnimationFrame(animationFrameId);
  window.removeEventListener("resize", resize);
});
</script>

<style scoped>
.particle-canvas {
  position: absolute;
  inset: 0;
  pointer-events: none;
  z-index: 2;
}
</style>
